import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setPage,
  setLimit,
  setSortBy,
  setDefaultSort,
} from "../../store/slices/documentTypeSlice.ts";
import { RootState } from "../../store/index.ts";
import ModalContainer from "../ModalContainer.tsx";
import DeleteDocumentType from "./DeleteDocumentType.tsx";
import EmptyImage from "../../assets/img/ic_content.svg";
import { fixedHeadStyle } from "../../utils/constanst.js";

interface DocumentTypeItem {
  id: number;
  name: string;
  obligatory: boolean;
  fields?: string[];
  maxDayToUpload?: number;
}

interface DocumentTypeTableProps {
  onEditClick?: (item: DocumentTypeItem) => void;
}

const DocumentTypeTable: React.FC<DocumentTypeTableProps> = ({ onEditClick }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [id, setId] = React.useState<number>(0);
  const [name, setName] = React.useState<string>("");

  const { total, page, limit, documentTypes, sortBy, defaultSort } =
    useSelector((state: RootState) => state.documentType);

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(event.target.value);
    dispatch(setLimit(newLimit));
    dispatch(setPage(0));
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const getIconSort = () => (
    <i
      className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
    />
  );

  const tableContainerStyle: React.CSSProperties = {
    overflow: "auto",
    maxHeight: "580px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  };

  const headerCellStyle: React.CSSProperties = {
    ...(fixedHeadStyle as React.CSSProperties),
    cursor: "pointer",
    padding: "14px 16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
  };

  const headerCellNoSort: React.CSSProperties = {
    ...(fixedHeadStyle as React.CSSProperties),
    padding: "14px 16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
  };

  const rowStyle = (index: number): React.CSSProperties => ({
    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
    transition: "background-color 0.15s ease",
  });

  const cellStyle: React.CSSProperties = {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#1f2937",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  };

  const getDaysChipStyle = (days: number): React.CSSProperties => {
    const isRed = days <= 5;
    const isYellow = days <= 10;
    return {
      backgroundColor: isRed ? "#fee2e2" : isYellow ? "#fef9c3" : "#dcfce7",
      color: isRed ? "#dc2626" : isYellow ? "#854d0e" : "#15803d",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "13px",
      fontWeight: 600,
      display: "inline-block",
      minWidth: "64px",
      textAlign: "center",
    };
  };

  return (
    <React.Fragment>
      <div className="table-responsive mb-3" style={tableContainerStyle}>
        <table
          className="table table-hover align-middle mb-0 text-nowrap"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th style={headerCellStyle} onClick={() => setSort("id")}>
                ID {sortBy === "id" && getIconSort()}
              </th>
              <th style={headerCellStyle} onClick={() => setSort("name")}>
                Nombre {sortBy === "name" && getIconSort()}
              </th>
              <th style={headerCellNoSort}>Obligatorio</th>
              <th style={headerCellNoSort}>Días máx. para subir</th>
              <th style={headerCellNoSort}>Campos</th>
              <th style={headerCellNoSort}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {documentTypes?.length > 0 ? (
              documentTypes.map((item, index) => (
                <tr
                  key={item.id}
                  style={rowStyle(index)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f9ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      index % 2 === 0 ? "#ffffff" : "#fafafa";
                  }}
                >
                  <td style={{ ...cellStyle, fontWeight: 500 }}>{item.id}</td>
                  <td style={cellStyle}>{item.name}</td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        backgroundColor: item.obligatory ? "#dcfce7" : "#f3f4f6",
                        color: item.obligatory ? "#15803d" : "#6b7280",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "13px",
                        fontWeight: 600,
                        display: "inline-block",
                      }}
                    >
                      {item.obligatory ? "Sí" : "No"}
                    </span>
                  </td>
                  <td style={cellStyle}>
                    <span style={getDaysChipStyle(item.maxDayToUpload ?? 0)}>
                      {item.maxDayToUpload ?? 0} días
                    </span>
                  </td>
                  <td style={{ ...cellStyle, color: "#6b7280" }}>
                    {item.fields?.length ? item.fields.join(", ") : "Sin campos"}
                  </td>
                  <td style={cellStyle}>
                    <a
                      href="#/"
                      className="btn btn-naranja"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onEditClick) onEditClick(item);
                      }}
                    >
                      <i className="fa fa-pencil"></i>
                    </a>
                    <a
                      href="#/"
                      className={`btn btn-rojo m-2 ${item.id <= 23 ? "disabled opacity-50 pe-none" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (item.id > 23) {
                          setId(item.id);
                          setName(item.name);
                          setShowModal(true);
                        }
                      }}
                    >
                      <i className="fa fa-circle-xmark"></i>
                    </a>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <div className="py-4">
                    <div className="d-flex">
                      <img src={EmptyImage} alt="" className="mx-auto w-250px" />
                    </div>
                    <h5 className="text-secondary text-center fs-20px">
                      Aún no existen tipos de documento
                    </h5>
                    <h5 className="text-center text-secondary fw-400 fs-15px">
                      Prueba añadiendo uno en el botón de 'Nuevo tipo'
                    </h5>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-md-flex align-items-center">
        <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
          Mostrando {Math.min(page * limit + documentTypes.length, total)} de{" "}
          {total} tipos de documento
        </div>
        <div className="d-flex align-items-center">
          <span className="me-2">Mostrar:</span>
          <select
            className="form-select me-3"
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <ul className="pagination mb-0 justify-content-center">
          <li className={`page-item ${page === 0 && "disabled"}`}>
            <a
              href="#/"
              className="page-link"
              onClick={(e) => {
                e.preventDefault();
                if (page > 0) handlePageChange(page - 1);
              }}
            >
              Anterior
            </a>
          </li>
          <li
            className={`page-item ${
              total <= page * limit + documentTypes.length && "disabled"
            }`}
          >
            <a
              href="#/"
              className="page-link"
              onClick={(e) => {
                e.preventDefault();
                if (total > page * limit + documentTypes.length)
                  handlePageChange(page + 1);
              }}
            >
              Siguiente
            </a>
          </li>
        </ul>
      </div>
      <ModalContainer
        open={showModal}
        closeFn={() => {
          setShowModal(false);
          setId(0);
          setName("");
        }}
        title={`Eliminar tipo de documento "${name}"`}
        children={
          <DeleteDocumentType
            name={name}
            id={id}
            closeFn={() => {
              setShowModal(false);
              setId(0);
              setName("");
            }}
          />
        }
      />
    </React.Fragment>
  );
};

export default DocumentTypeTable;
